import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Save, X, Package, Tag, Sparkles, 
  ChevronDown, ChevronUp, Globe, Euro, Clock, Image, 
  Check, AlertCircle, Loader2, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import AdminPinGuard from '../components/AdminPinGuard';
import ImageUploader from '../components/ImageUploader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const translations = {
  en: {
    title: 'Content Management',
    subtitle: 'Manage services, promotions and products',
    tabs: { services: 'Services', promotions: 'Promotions', products: 'Products' },
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading...',
    noData: 'No items yet',
    confirmDelete: 'Are you sure you want to delete this item?',
    fields: {
      category: 'Category',
      name: 'Name',
      description: 'Description',
      price: 'Price',
      duration: 'Duration',
      image: 'Image URL',
      stock: 'Stock',
      active: 'Active',
      discountType: 'Discount Type',
      discountValue: 'Discount Value',
      promoCode: 'Promo Code',
      startDate: 'Start Date',
      endDate: 'End Date',
    },
    discountTypes: { percentage: 'Percentage (%)', fixed: 'Fixed Amount (€)' },
    success: { created: 'Created successfully', updated: 'Updated successfully', deleted: 'Deleted successfully' },
    error: 'An error occurred',
  },
  fr: {
    title: 'Gestion du Contenu',
    subtitle: 'Gérez les services, promotions et produits',
    tabs: { services: 'Services', promotions: 'Promotions', products: 'Produits' },
    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    loading: 'Chargement...',
    noData: 'Aucun élément',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer cet élément ?',
    fields: {
      category: 'Catégorie',
      name: 'Nom',
      description: 'Description',
      price: 'Prix',
      duration: 'Durée',
      image: 'URL de l\'image',
      stock: 'Stock',
      active: 'Actif',
      discountType: 'Type de réduction',
      discountValue: 'Valeur de la réduction',
      promoCode: 'Code Promo',
      startDate: 'Date de début',
      endDate: 'Date de fin',
    },
    discountTypes: { percentage: 'Pourcentage (%)', fixed: 'Montant fixe (€)' },
    success: { created: 'Créé avec succès', updated: 'Mis à jour avec succès', deleted: 'Supprimé avec succès' },
    error: 'Une erreur est survenue',
  },
  pt: {
    title: 'Gestão de Conteúdo',
    subtitle: 'Gerir serviços, promoções e produtos',
    tabs: { services: 'Serviços', promotions: 'Promoções', products: 'Produtos' },
    add: 'Adicionar',
    edit: 'Editar',
    delete: 'Eliminar',
    save: 'Guardar',
    cancel: 'Cancelar',
    loading: 'A carregar...',
    noData: 'Sem itens',
    confirmDelete: 'Tem a certeza que quer eliminar este item?',
    fields: {
      category: 'Categoria',
      name: 'Nome',
      description: 'Descrição',
      price: 'Preço',
      duration: 'Duração',
      image: 'URL da imagem',
      stock: 'Stock',
      active: 'Ativo',
      discountType: 'Tipo de desconto',
      discountValue: 'Valor do desconto',
      promoCode: 'Código Promocional',
      startDate: 'Data de início',
      endDate: 'Data de fim',
    },
    discountTypes: { percentage: 'Percentagem (%)', fixed: 'Montante fixo (€)' },
    success: { created: 'Criado com sucesso', updated: 'Atualizado com sucesso', deleted: 'Eliminado com sucesso' },
    error: 'Ocorreu um erro',
  },
  de: {
    title: 'Content-Verwaltung',
    subtitle: 'Verwalten Sie Dienstleistungen, Aktionen und Produkte',
    tabs: { services: 'Dienstleistungen', promotions: 'Aktionen', products: 'Produkte' },
    add: 'Hinzufügen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    loading: 'Laden...',
    noData: 'Keine Einträge',
    confirmDelete: 'Sind Sie sicher, dass Sie diesen Eintrag löschen möchten?',
    fields: {
      category: 'Kategorie',
      name: 'Name',
      description: 'Beschreibung',
      price: 'Preis',
      duration: 'Dauer',
      image: 'Bild-URL',
      stock: 'Bestand',
      active: 'Aktiv',
      discountType: 'Rabattart',
      discountValue: 'Rabattwert',
      promoCode: 'Promo-Code',
      startDate: 'Startdatum',
      endDate: 'Enddatum',
    },
    discountTypes: { percentage: 'Prozentsatz (%)', fixed: 'Festbetrag (€)' },
    success: { created: 'Erfolgreich erstellt', updated: 'Erfolgreich aktualisiert', deleted: 'Erfolgreich gelöscht' },
    error: 'Ein Fehler ist aufgetreten',
  },
  es: {
    title: 'Gestión de Contenido',
    subtitle: 'Gestionar servicios, promociones y productos',
    tabs: { services: 'Servicios', promotions: 'Promociones', products: 'Productos' },
    add: 'Añadir',
    edit: 'Editar',
    delete: 'Eliminar',
    save: 'Guardar',
    cancel: 'Cancelar',
    loading: 'Cargando...',
    noData: 'Sin elementos',
    confirmDelete: '¿Está seguro de que desea eliminar este elemento?',
    fields: {
      category: 'Categoría',
      name: 'Nombre',
      description: 'Descripción',
      price: 'Precio',
      duration: 'Duración',
      image: 'URL de la imagen',
      stock: 'Stock',
      active: 'Activo',
      discountType: 'Tipo de descuento',
      discountValue: 'Valor del descuento',
      promoCode: 'Código Promocional',
      startDate: 'Fecha de inicio',
      endDate: 'Fecha de fin',
    },
    discountTypes: { percentage: 'Porcentaje (%)', fixed: 'Monto fijo (€)' },
    success: { created: 'Creado con éxito', updated: 'Actualizado con éxito', deleted: 'Eliminado con éxito' },
    error: 'Ha ocurrido un error',
  },
};

// Multi-language input component
const MultiLangInput = ({ label, fieldName, values, onChange, isTextarea = false }) => {
  const [expanded, setExpanded] = useState(false);
  const InputComponent = isTextarea ? 'textarea' : 'input';
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-kamit-brown flex items-center gap-2">
          <Globe className="h-4 w-4" />
          {label}
        </label>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-kamit-gold hover:text-kamit-brown flex items-center gap-1"
        >
          {expanded ? 'Hide' : 'Show all languages'}
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </div>
      
      {/* Primary language (English) always visible */}
      <div className="flex items-center gap-2">
        <span className="text-lg">🇬🇧</span>
        <InputComponent
          value={values[`${fieldName}_en`] || ''}
          onChange={(e) => onChange(`${fieldName}_en`, e.target.value)}
          placeholder="English"
          className={`flex-1 p-2 border border-kamit-brown/20 rounded-lg focus:ring-2 focus:ring-kamit-gold focus:border-transparent text-sm ${isTextarea ? 'min-h-[80px]' : ''}`}
        />
      </div>
      
      {/* Other languages */}
      {expanded && LANGUAGES.filter(l => l.code !== 'en').map((lang) => (
        <div key={lang.code} className="flex items-center gap-2">
          <span className="text-lg">{lang.flag}</span>
          <InputComponent
            value={values[`${fieldName}_${lang.code}`] || ''}
            onChange={(e) => onChange(`${fieldName}_${lang.code}`, e.target.value)}
            placeholder={lang.label}
            className={`flex-1 p-2 border border-kamit-brown/20 rounded-lg focus:ring-2 focus:ring-kamit-gold focus:border-transparent text-sm ${isTextarea ? 'min-h-[80px]' : ''}`}
          />
        </div>
      ))}
    </div>
  );
};

// Toast notification component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-70">
      <X className="h-4 w-4" />
    </button>
  </div>
);

const AdminCMSPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [activeTab, setActiveTab] = useState('services');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({ service_categories: [], product_categories: [] });
  const [services, setServices] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Get admin PIN from session storage
  const adminPin = sessionStorage.getItem('admin_pin') || '';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, servRes, promoRes, prodRes] = await Promise.all([
        axios.get(`${API}/admin/cms/categories`),
        axios.get(`${API}/admin/cms/services`),
        axios.get(`${API}/admin/cms/promotions`),
        axios.get(`${API}/admin/cms/products`),
      ]);
      setCategories(catRes.data);
      setServices(servRes.data);
      setPromotions(promoRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showToast(t.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = (type) => {
    let newItem = {};
    if (type === 'services') {
      newItem = {
        category: categories.service_categories[0]?.id || '',
        name_en: '', name_fr: '', name_pt: '', name_de: '', name_es: '',
        description_en: '', description_fr: '', description_pt: '', description_de: '', description_es: '',
        price: '', duration: '', image: '', active: true
      };
    } else if (type === 'promotions') {
      newItem = {
        title_en: '', title_fr: '', title_pt: '', title_de: '', title_es: '',
        description_en: '', description_fr: '', description_pt: '', description_de: '', description_es: '',
        discount_type: 'percentage', discount_value: 10,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
        promo_code: '', image: '', active: true
      };
    } else if (type === 'products') {
      newItem = {
        category: categories.product_categories[0]?.id || '',
        name_en: '', name_fr: '', name_pt: '', name_de: '', name_es: '',
        description_en: '', description_fr: '', description_pt: '', description_de: '', description_es: '',
        price: 0, stock: 0, image: '', active: true
      };
    }
    setEditingItem(newItem);
    setIsNew(true);
  };

  const handleEdit = (item) => {
    setEditingItem({ ...item });
    setIsNew(false);
  };

  const handleCancel = () => {
    setEditingItem(null);
    setIsNew(false);
  };

  const handleFieldChange = (field, value) => {
    setEditingItem(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let endpoint = '';
      let data = editingItem;
      
      if (activeTab === 'services') {
        endpoint = isNew ? `${API}/admin/cms/services` : `${API}/admin/cms/services/${editingItem.id}`;
      } else if (activeTab === 'promotions') {
        endpoint = isNew ? `${API}/admin/cms/promotions` : `${API}/admin/cms/promotions/${editingItem.id}`;
      } else if (activeTab === 'products') {
        endpoint = isNew ? `${API}/admin/cms/products` : `${API}/admin/cms/products/${editingItem.id}`;
      }

      if (isNew) {
        await axios.post(endpoint, data);
        showToast(t.success.created);
      } else {
        await axios.put(endpoint, data);
        showToast(t.success.updated);
      }
      
      setEditingItem(null);
      setIsNew(false);
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      showToast(t.error, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t.confirmDelete)) return;
    
    try {
      let endpoint = '';
      if (activeTab === 'services') {
        endpoint = `${API}/admin/cms/services/${item.id}`;
      } else if (activeTab === 'promotions') {
        endpoint = `${API}/admin/cms/promotions/${item.id}`;
      } else if (activeTab === 'products') {
        endpoint = `${API}/admin/cms/products/${item.id}`;
      }
      
      await axios.delete(endpoint);
      showToast(t.success.deleted);
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      showToast(t.error, 'error');
    }
  };

  const getCategoryName = (catId, type) => {
    const cats = type === 'service' ? categories.service_categories : categories.product_categories;
    const cat = cats.find(c => c.id === catId);
    return cat ? (cat[`name_${language}`] || cat.name_en) : catId;
  };

  const renderServiceForm = () => (
    <div className="space-y-4 bg-white rounded-xl p-6 border border-kamit-brown/10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-kamit-brown">{t.fields.category}</label>
          <select
            value={editingItem.category}
            onChange={(e) => handleFieldChange('category', e.target.value)}
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          >
            {categories.service_categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat[`name_${language}`] || cat.name_en}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editingItem.active}
              onChange={(e) => handleFieldChange('active', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm font-medium text-kamit-brown">{t.fields.active}</span>
          </label>
        </div>
      </div>
      
      <MultiLangInput label={t.fields.name} fieldName="name" values={editingItem} onChange={handleFieldChange} />
      <MultiLangInput label={t.fields.description} fieldName="description" values={editingItem} onChange={handleFieldChange} isTextarea />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-sm font-medium text-kamit-brown flex items-center gap-2">
            <Euro className="h-4 w-4" /> {t.fields.price}
          </label>
          <input
            type="text"
            value={editingItem.price}
            onChange={(e) => handleFieldChange('price', e.target.value)}
            placeholder="e.g., 55€"
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-kamit-brown flex items-center gap-2">
            <Clock className="h-4 w-4" /> {t.fields.duration}
          </label>
          <input
            type="text"
            value={editingItem.duration}
            onChange={(e) => handleFieldChange('duration', e.target.value)}
            placeholder="e.g., 60min"
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          />
        </div>
      </div>
      
      <ImageUploader
        value={editingItem.image}
        onChange={(url) => handleFieldChange('image', url)}
        adminPin={adminPin}
        label={t.fields.image}
      />
    </div>
  );

  const renderPromotionForm = () => (
    <div className="space-y-4 bg-white rounded-xl p-6 border border-kamit-brown/10">
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={editingItem.active}
            onChange={(e) => handleFieldChange('active', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm font-medium text-kamit-brown">{t.fields.active}</span>
        </label>
      </div>
      
      <MultiLangInput label={t.fields.name} fieldName="title" values={editingItem} onChange={handleFieldChange} />
      <MultiLangInput label={t.fields.description} fieldName="description" values={editingItem} onChange={handleFieldChange} isTextarea />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-sm font-medium text-kamit-brown">{t.fields.discountType}</label>
          <select
            value={editingItem.discount_type}
            onChange={(e) => handleFieldChange('discount_type', e.target.value)}
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          >
            <option value="percentage">{t.discountTypes.percentage}</option>
            <option value="fixed">{t.discountTypes.fixed}</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-kamit-brown">{t.fields.discountValue}</label>
          <input
            type="number"
            value={editingItem.discount_value}
            onChange={(e) => handleFieldChange('discount_value', parseFloat(e.target.value))}
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-kamit-brown">{t.fields.promoCode}</label>
          <input
            type="text"
            value={editingItem.promo_code}
            onChange={(e) => handleFieldChange('promo_code', e.target.value.toUpperCase())}
            placeholder="SUMMER2026"
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-kamit-brown">{t.fields.startDate}</label>
          <input
            type="date"
            value={editingItem.start_date}
            onChange={(e) => handleFieldChange('start_date', e.target.value)}
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-kamit-brown">{t.fields.endDate}</label>
          <input
            type="date"
            value={editingItem.end_date}
            onChange={(e) => handleFieldChange('end_date', e.target.value)}
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          />
        </div>
      </div>
      
      <ImageUploader
        value={editingItem.image}
        onChange={(url) => handleFieldChange('image', url)}
        adminPin={adminPin}
        label={t.fields.image}
      />
      
      {/* Featured Toggle */}
      <div className="flex items-center gap-3 p-4 bg-kamit-gold/10 rounded-lg border border-kamit-gold/30">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={editingItem.featured || false}
            onChange={(e) => handleFieldChange('featured', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-kamit-gold"></div>
        </label>
        <div>
          <span className="text-sm font-medium text-kamit-brown">
            {language === 'fr' ? 'Mise en avant sur la page d\'accueil' : 
             language === 'pt' ? 'Destaque na página inicial' : 
             'Featured on Homepage'}
          </span>
          <p className="text-xs text-kamit-brown/60">
            {language === 'fr' ? 'Cette promotion sera affichée en priorité sur la page d\'accueil' : 
             language === 'pt' ? 'Esta promoção será exibida com prioridade na página inicial' : 
             'This promotion will be displayed as priority on the homepage'}
          </p>
        </div>
      </div>
    </div>
  );

  const renderProductForm = () => (
    <div className="space-y-4 bg-white rounded-xl p-6 border border-kamit-brown/10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-kamit-brown">{t.fields.category}</label>
          <select
            value={editingItem.category}
            onChange={(e) => handleFieldChange('category', e.target.value)}
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          >
            {categories.product_categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat[`name_${language}`] || cat.name_en}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editingItem.active}
              onChange={(e) => handleFieldChange('active', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm font-medium text-kamit-brown">{t.fields.active}</span>
          </label>
        </div>
      </div>
      
      <MultiLangInput label={t.fields.name} fieldName="name" values={editingItem} onChange={handleFieldChange} />
      <MultiLangInput label={t.fields.description} fieldName="description" values={editingItem} onChange={handleFieldChange} isTextarea />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-sm font-medium text-kamit-brown flex items-center gap-2">
            <Euro className="h-4 w-4" /> {t.fields.price}
          </label>
          <input
            type="number"
            step="0.01"
            value={editingItem.price}
            onChange={(e) => handleFieldChange('price', parseFloat(e.target.value))}
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-kamit-brown flex items-center gap-2">
            <Package className="h-4 w-4" /> {t.fields.stock}
          </label>
          <input
            type="number"
            value={editingItem.stock}
            onChange={(e) => handleFieldChange('stock', parseInt(e.target.value))}
            className="w-full p-2 border border-kamit-brown/20 rounded-lg mt-1"
          />
        </div>
      </div>
      
      <ImageUploader
        value={editingItem.image}
        onChange={(url) => handleFieldChange('image', url)}
        adminPin={adminPin}
        label={t.fields.image}
      />
    </div>
  );

  const renderList = (items, type) => {
    if (items.length === 0) {
      return (
        <div className="text-center py-12 text-kamit-brown/60">
          {t.noData}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {items.map(item => (
          <div 
            key={item.id} 
            className={`bg-white rounded-xl p-4 border ${item.active ? 'border-kamit-brown/10' : 'border-red-200 bg-red-50/30'} flex items-center justify-between gap-4`}
          >
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {item.image && (
                <img src={item.image} alt="" className="h-14 w-14 object-cover rounded-lg flex-shrink-0" />
              )}
              <div className="min-w-0">
                <h4 className="font-medium text-kamit-brown truncate">
                  {item[`name_${language}`] || item[`title_${language}`] || item.name_en || item.title_en}
                </h4>
                <p className="text-sm text-kamit-brown/60">
                  {type === 'services' && <span>{getCategoryName(item.category, 'service')} • {item.price}</span>}
                  {type === 'promotions' && <span>{item.discount_value}{item.discount_type === 'percentage' ? '%' : '€'} • {item.start_date} → {item.end_date}</span>}
                  {type === 'products' && <span>{getCategoryName(item.category, 'product')} • {item.price}€ • Stock: {item.stock}</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!item.active && (
                <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Inactive</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(item)}
                className="text-kamit-gold hover:text-kamit-brown"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(item)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <AdminPinGuard>
      <div className="min-h-screen bg-kamit-beige pt-20 pb-12" data-testid="admin-cms-page">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl text-kamit-brown">{t.title}</h1>
            <p className="text-kamit-brown/60">{t.subtitle}</p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'services', icon: Sparkles, label: t.tabs.services },
              { id: 'promotions', icon: Tag, label: t.tabs.promotions },
              { id: 'products', icon: Package, label: t.tabs.products },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setEditingItem(null); }}
                className={`px-4 py-2 rounded-full flex items-center gap-2 transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-kamit-brown text-kamit-beige' 
                    : 'bg-white text-kamit-brown border border-kamit-brown/20 hover:bg-kamit-brown/10'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Action Bar */}
          <div className="flex justify-between items-center mb-6">
            <Button
              onClick={() => handleAdd(activeTab)}
              className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full"
              disabled={editingItem !== null}
              data-testid="btn-add"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.add}
            </Button>
            <Button
              variant="outline"
              onClick={fetchData}
              className="border-kamit-brown/20"
              data-testid="btn-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Edit Form */}
          {editingItem && (
            <div className="mb-6">
              <div className="bg-kamit-gold/10 rounded-xl p-4 mb-4">
                <h3 className="font-medium text-kamit-brown mb-4">
                  {isNew ? t.add : t.edit}
                </h3>
                
                {activeTab === 'services' && renderServiceForm()}
                {activeTab === 'promotions' && renderPromotionForm()}
                {activeTab === 'products' && renderProductForm()}
                
                <div className="flex justify-end gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="border-kamit-brown/20"
                    data-testid="btn-cancel"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t.cancel}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-kamit-brown hover:bg-kamit-dark text-kamit-beige"
                    data-testid="btn-save"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {t.save}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-kamit-gold mx-auto" />
              <p className="text-kamit-brown/60 mt-2">{t.loading}</p>
            </div>
          ) : (
            <>
              {activeTab === 'services' && renderList(services, 'services')}
              {activeTab === 'promotions' && renderList(promotions, 'promotions')}
              {activeTab === 'products' && renderList(products, 'products')}
            </>
          )}
        </div>

        {/* Toast */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </AdminPinGuard>
  );
};

export default AdminCMSPage;
